import { Alert, Box, Heading2, Text } from "@nulogy/components";
import FormButtons from "components/controls/FormButtons";
import Input from "components/controls/Input";
import Select from "components/controls/Select";
import SettingsForm from "components/controls/SettingsForm";
import PageWithBreadcrumbs from "components/pages/PageWithBreadcrumbs";
import get from "lodash/get";
import React from "react";
import UnblockUserButton from "./UnblockUserButton";

const TURKISH_CHARACTERS = "İÖÜŞÇĞıöüşçğ";
const SUPPORTED_CHARACTERS = [TURKISH_CHARACTERS];

export default function UserEditView({
  refetchSites,
  onCancel,
  onDelete,
  onSave,
  user,
  roles,
  companies,
  assignableSites,
  siteId,
  initialRole,
  initialCompanyId,
  canUpdateCompany,
  fetchAuthenticationUser,
  authenticationUserExists,
}) {
  const initialAllowedSiteIds = siteId ? [siteId] : (user.allowedSites || []).map((site) => site.id);
  const initialValues = {
    ...user,
    allowedSiteIds: initialAllowedSiteIds,
    company: { id: initialCompanyId },
    confirmPassword: "",
    role: initialRole,
  };
  const newRecord = !get(user, "id", null);
  const title = `${newRecord ? "Create" : "Update"} user`;
  const companyOptions = companies.map((company) => ({ label: company.name, value: company.id }));

  const assignableSitesOptions = assignableSites.map((site) => ({
    label: `${site.company.name} - ${site.name}`,
    value: site.id,
  }));

  const handleCompanyChange =
    (role) =>
    ({ value: newCompanyId, setFieldValue }) => {
      setFieldValue("allowedSiteIds", []);
      refetchSites({ companyId: newCompanyId, role });
    };

  const handleRoleChange =
    (companyId) =>
    ({ value: newRole, setFieldValue }) => {
      switch (newRole) {
        case "nulogy_admin":
        case "nulogy_restricted":
          setFieldValue("company[id]", null);
          setFieldValue("allowedSiteIds", []);
          refetchSites({ role: newRole });
          break;
        case "planner":
        case "readonly":
          setFieldValue("company[id]", companyId);
          refetchSites({ companyId, role: newRole });
          break;
        default:
          throw new Error(`Unhandled role: ${newRole}`);
      }
    };

  const emailRegex = `[${SUPPORTED_CHARACTERS.join()}A-Za-z0-9_!#$%&'*+/=?\`{|}~^.-]+@[A-Za-z0-9.-]+`;

  return (
    <PageWithBreadcrumbs
      className="userEditView spec-user-create-edit-page"
      extraBreadcrumbs={[{ label: "Users", path: "/users" }]}
      title={title}
    >
      <SettingsForm {...{ initialValues, onSave }}>
        {({ values }) => {
          const passwordRequired = !isNulogyUser(values.email) && !authenticationUserExists;

          return (
            <>
              {newRecord && !passwordRequired && (
                <Alert type="informative">
                  <Text>
                    This user already exists in Auth0 (which enables them to log in via SSO) or has an @nulogy email
                    address. As a result, they will not receive a confirmation email upon creation and should log in
                    using their existing credentials.
                  </Text>
                </Alert>
              )}
              <Input
                disabled={!newRecord}
                field="email"
                labelText="Email"
                onBlur={fetchAuthenticationUser}
                pattern={emailRegex}
                title="Please provide a valid email address"
                type="text"
              />
              <Box marginBottom="x3">
                <Select
                  className="spec-user-role"
                  field="role"
                  labelText="Role"
                  maxHeight="250"
                  onChange={handleRoleChange(values.company.id)}
                  options={roles}
                />
              </Box>
              <Box data-testid="user-company" marginBottom="x2" style={{ width: "440px" }}>
                {shouldDisplayCompanySelector(values.role) && (
                  <Select
                    className="spec-user-company"
                    disabled={!canUpdateCompany && !newRecord}
                    field="company[id]"
                    labelText="Company"
                    maxHeight="250"
                    onChange={handleCompanyChange(values.role)}
                    options={companyOptions}
                  />
                )}
              </Box>
              <Box marginBottom="x3">
                {shouldDisplaySitesSelector(values.role, values.company.id) && (
                  <Select
                    className="spec-assigned-sites-multiselect"
                    field="allowedSiteIds"
                    labelText="Assigned sites"
                    maxHeight="250"
                    multiselect
                    options={assignableSitesOptions}
                    placeholder="Assign sites..."
                  />
                )}
              </Box>
              <FormButtons
                deleteConfirmationMessage="Are you sure you want to delete this user? This action is irreversible."
                onCancel={onCancel}
                onDelete={newRecord ? null : onDelete}
              />
            </>
          );
        }}
      </SettingsForm>
      {!newRecord && (
        <>
          <hr />
          <Heading2 mt="x2">Authentication</Heading2>
          <UnblockUserButton user={user} />
        </>
      )}
    </PageWithBreadcrumbs>
  );
}

function shouldDisplayCompanySelector(role) {
  switch (role) {
    case "nulogy_admin":
    case "nulogy_restricted":
    case undefined:
      return false;
    case "planner":
    case "readonly":
      return true;
    default:
      throw new Error(`Unhandled role: ${role}`);
  }
}

function shouldDisplaySitesSelector(role, companyId) {
  switch (role) {
    case "nulogy_admin":
    case undefined:
      return false;
    case "nulogy_restricted":
      return true;
    case "planner":
    case "readonly":
      return companyId;
    default:
      throw new Error(`Unhandled role: ${role}`);
  }
}

function isNulogyUser(email) {
  return (email || "").endsWith("@nulogy.com");
}
